import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, Shield } from 'lucide-react';
import SiteLogo from '../../components/Layout/SiteLogo';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';

const pageShell = 'min-h-[100dvh] bg-slate-100';

const ContratoAssinaturaForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [clientLabel, setClientLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [signatureMode, setSignatureMode] = useState<'drawn' | 'typed'>('drawn');
  const [typedName, setTypedName] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  const signaturePadRef = useRef<SignaturePadHandle>(null);

  useEffect(() => {
    if (!token) {
      setErrorMsg('Link inválido.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/contrato/public/${token}`);
        setValid(res.data.valid);
        setAlreadySigned(res.data.alreadySigned);
        setClientLabel(res.data.clientLabel || '');
        setExpiresAt(res.data.expiresAt || null);
      } catch (err: unknown) {
        setValid(false);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Este link não está mais disponível.';
        setErrorMsg(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async () => {
    setSubmitError('');
    if (!consentAccepted) {
      setSubmitError('É necessário aceitar os termos para assinar.');
      return;
    }
    let payload: Record<string, unknown>;
    if (signatureMode === 'drawn') {
      if (signaturePadRef.current?.isEmpty()) {
        setSubmitError('Desenhe sua assinatura antes de continuar.');
        return;
      }
      payload = {
        signatureType: 'drawn',
        signatureImageData: signaturePadRef.current?.toDataURL(),
        consentAccepted: true
      };
    } else {
      if (!typedName.trim()) {
        setSubmitError('Digite seu nome completo antes de continuar.');
        return;
      }
      payload = { signatureType: 'typed', signatureTypedName: typedName.trim(), consentAccepted: true };
    }

    setSubmitting(true);
    try {
      await axios.post(`/contrato/public/${token}/assinar`, payload);
      setDone(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível registrar sua assinatura. Tente novamente.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label="Carregando" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link indisponível</h1>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (alreadySigned || done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Contrato assinado</h1>
          <p className="text-gray-600">
            {done
              ? 'Sua assinatura foi registrada com sucesso. Você receberá uma cópia do contrato assinado por e-mail.'
              : 'Este contrato já foi assinado.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}>
      <div className="mx-auto w-full max-w-3xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
        <header className="text-center text-gray-900 mb-5 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <SiteLogo size="lg" nameClassName="font-bold text-gray-900 text-xl sm:text-2xl" className="justify-center" />
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight px-1">Assinatura de contrato</h1>
          {clientLabel && (
            <p className="text-gray-600 text-sm sm:text-base mt-2">Olá, {clientLabel}</p>
          )}
          {expiresAt && (
            <p className="text-gray-400 text-xs mt-1">
              Link válido até {new Date(expiresAt).toLocaleDateString('pt-BR')}
            </p>
          )}
        </header>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Revise o contrato</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ height: '60vh', minHeight: 320 }}>
            <iframe
              src={`${axios.defaults.baseURL || '/api'}/contrato/public/${token}/preview-pdf`}
              title="Contrato para assinatura"
              className="w-full h-full"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Assine o contrato</h2>

          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setSignatureMode('drawn')}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                signatureMode === 'drawn'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              Desenhar assinatura
            </button>
            <button
              type="button"
              onClick={() => setSignatureMode('typed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                signatureMode === 'typed'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              Digitar nome
            </button>
          </div>

          {signatureMode === 'drawn' ? (
            <SignaturePad ref={signaturePadRef} />
          ) : (
            <label className="block">
              <span className="text-sm text-gray-600">Nome completo</span>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Digite seu nome completo"
                style={{ fontFamily: "'Brush Script MT', cursive" }}
                className="mt-1 w-full text-2xl border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
          )}

          <label className="flex items-start gap-2 mt-4 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              className="mt-1"
            />
            <span>Li e concordo com os termos do contrato acima, e declaro que esta assinatura eletrônica tem a mesma validade de uma assinatura manuscrita.</span>
          </label>

          {submitError && <p className="text-sm text-red-600 mt-3">{submitError}</p>}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Assinar contrato
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContratoAssinaturaForm;
